// WYHSIAR GAMES WORLD - CLIENT-SIDE LOCAL GAME & PARTY ROOM SIMULATION BRIDGE
// Enables 100% playable instant solo, bot, and party lounge games directly in-browser on static CDN hosts (Cloudflare Pages)

import { LudoEngine } from '../server/gameEngines/ludoEngine.js';
import { PoolEngine } from '../server/gameEngines/poolEngine.js';
import { SoccerEngine } from '../server/gameEngines/soccerEngine.js';
import { BombTagEngine } from '../server/gameEngines/bombTagEngine.js';
import { BattleshipEngine } from '../server/gameEngines/battleshipEngine.js';
import { AirHockeyEngine } from '../server/gameEngines/airHockeyEngine.js';
import { ArcheryEngine } from '../server/gameEngines/archeryEngine.js';
import { TriviaEngine } from '../server/gameEngines/triviaEngine.js';
import { KartEngine } from '../server/gameEngines/kartEngine.js';
import { SnakeEngine } from '../server/gameEngines/snakeEngine.js';
import { TankEngine } from '../server/gameEngines/tankEngine.js';
import { TicTacToeEngine } from '../server/gameEngines/tictactoeEngine.js';
import { Connect4Engine } from '../server/gameEngines/connect4Engine.js';
import { PongEngine } from '../server/gameEngines/pongEngine.js';
import { UnoEngine } from '../server/gameEngines/unoEngine.js';
import { SpyEngine } from '../server/gameEngines/spyEngine.js';
import { WordBombEngine } from '../server/gameEngines/wordBombEngine.js';
import { TetrisEngine } from '../server/gameEngines/tetrisEngine.js';
import { SpaceEngine } from '../server/gameEngines/spaceEngine.js';
import { DrawGuessEngine } from '../server/gameEngines/drawGuessEngine.js';

export function generateRandomRoomCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 4; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return `WP-${code}`;
}

export class LocalSocketBridge {
  constructor(playerName = 'Player 1', avatar = '🦊') {
    this.id = `local_player_${Math.floor(Math.random() * 10000)}`;
    this.playerName = playerName;
    this.avatar = avatar;
    this.connected = true;
    this.listeners = new Map(); // event -> array of callbacks
    this.engineInstance = null;
    this.room = null;
    this.botNames = [
      '🤖 CyberBot', '🤖 RoboChamp', '🦊 StarFox', '🐼 PandaX', '🐯 TigerPro',
      '🐸 PixelFrog', '👽 AlienAce', '🐙 OctoNinja', '🦁 LeoKnight', '🐻 BearGamer',
      '🦄 MysticHorn', '🐉 BlazeDrake', '🦅 SkyHawk', '🐺 ShadowWolf', '🐬 AquaDolphin',
      '🐰 TurboBunny', '🐵 ApeLegend', '🦖 DinoRex', '🦹 PhantomX', '⚡ VoltRider'
    ];
    this.botAvatars = [
      '🤖', '🦊', '🐼', '🐯', '🐸', '👽', '🐙', '👾', '🦁', '🐻',
      '🦄', '🐉', '🦅', '🐺', '🐬', '🐰', '🐵', '🦖', '🦹', '⚡'
    ];
    this.playerColors = [
      '#eab308', '#38bdf8', '#22c55e', '#ef4444', '#a855f7',
      '#ec4899', '#f97316', '#06b6d4', '#84cc16', '#e11d48',
      '#6366f1', '#14b8a6', '#f43f5e', '#8b5cf6', '#d97706',
      '#10b981', '#0ea5e9', '#d946ef', '#f59e0b', '#64748b'
    ];

    // Mock server IO interface for engines
    this.io = {
      to: () => ({
        emit: (event, data) => {
          this.triggerLocal(event, data);
        }
      })
    };
  }

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  off(event, callback) {
    if (!this.listeners.has(event)) return;
    if (!callback) {
      this.listeners.delete(event);
    } else {
      const arr = this.listeners.get(event).filter(cb => cb !== callback);
      this.listeners.set(event, arr);
    }
  }

  triggerLocal(event, data) {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach(cb => {
        try { cb(data); } catch (e) { console.error(`Error in local socket event ${event}:`, e); }
      });
    }
  }

  createLocalPartyRoom(initialGameId = 'drawGuess', customSettings = {}) {
    const roomCode = generateRandomRoomCode();
    const targetPlayers = customSettings.targetPlayers || 4;
    this.room = {
      code: roomCode,
      hostId: this.id,
      currentGame: initialGameId,
      maxPlayers: customSettings.maxPlayers || Math.max(targetPlayers, 20),
      settings: {
        roundTimer: customSettings.roundTimer || 60,
        totalRounds: customSettings.totalRounds || 3,
        targetPlayers: targetPlayers,
        fillBots: customSettings.fillBots !== undefined ? customSettings.fillBots : false
      },
      // ONLY the creator starts in the room - no hardcoded bots!
      players: [
        {
          id: this.id,
          name: this.playerName,
          avatar: this.avatar,
          color: this.playerColors[0],
          isHost: true,
          isReady: true,
          isBot: false,
          seatIndex: 0,
          score: 0
        }
      ]
    };

    setTimeout(() => {
      this.triggerLocal('roomJoined', { room: this.room, isHost: true });
    }, 50);

    return this.room;
  }

  addBot() {
    if (!this.room) return;
    const limit = this.room.settings?.targetPlayers || this.room.maxPlayers || 20;
    if (this.room.players.length >= limit || this.room.players.length >= this.room.maxPlayers) return;

    const seatIndex = this.room.players.length;
    const botIdx = Math.floor(Math.random() * this.botNames.length);

    const newBot = {
      id: `bot_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
      name: this.botNames[botIdx] || `🤖 Bot ${seatIndex + 1}`,
      avatar: this.botAvatars[botIdx] || '🤖',
      color: this.playerColors[seatIndex % this.playerColors.length],
      isHost: false,
      isReady: true,
      isBot: true,
      seatIndex,
      score: 0
    };

    this.room.players.push(newBot);
    this.triggerLocal('roomUpdated', { room: this.room });
  }

  removePlayer(targetId) {
    if (!this.room) return;
    const idx = this.room.players.findIndex(p => p.id === targetId);
    if (idx !== -1 && !this.room.players[idx].isHost) {
      this.room.players.splice(idx, 1);
      this.room.players.forEach((p, i) => { p.seatIndex = i; });
      this.triggerLocal('roomUpdated', { room: this.room });
    }
  }

  emit(event, data = {}) {
    // Room management actions
    if (event === 'createRoom') {
      this.createLocalPartyRoom('drawGuess', data);
      return;
    }

    if (event === 'addBot') {
      this.addBot();
      return;
    }

    if (event === 'removePlayer') {
      this.removePlayer(data.targetId);
      return;
    }

    if (event === 'updateRoomSettings') {
      if (this.room) {
        this.room.settings = { ...this.room.settings, ...data };
        this.triggerLocal('roomUpdated', { room: this.room });
      }
      return;
    }

    if (event === 'selectAndLaunchGame') {
      const gameId = data.gameId || 'drawGuess';
      this.startLocalGame(gameId, this.room?.settings);
      this.triggerLocal('gameLaunched', { gameId, room: this.room });
      return;
    }

    if (event === 'returnToLobby') {
      if (this.engineInstance?.destroy) {
        this.engineInstance.destroy();
        this.engineInstance = null;
      }
      if (this.room) {
        this.room.currentGame = null;
        this.triggerLocal('returnedToLobby', { room: this.room });
      }
      return;
    }

    if (event === 'toggleReady') {
      if (this.room) {
        const me = this.room.players.find(p => p.id === this.id);
        if (me) me.isReady = !me.isReady;
        this.triggerLocal('roomUpdated', { room: this.room });
      }
      return;
    }

    if (!this.engineInstance) return;

    // Route client gameplay actions directly to local engine instance
    // Ludo
    if (event === 'ludo_roll') this.engineInstance.rollDice?.(this.id);
    else if (event === 'ludo_move') this.engineInstance.moveToken?.(this.id, data.tokenIndex);
    else if (event === 'ludo_setMatchSize') this.engineInstance.setMatchSize?.(data.matchSize);
    else if (event === 'ludo_customize') this.engineInstance.customizePlayer?.(this.id, data);
    else if (event === 'ludo_enterGame') this.engineInstance.enterGame?.(this.id);
    else if (event === 'ludo_leaveMatch') this.engineInstance.handleLeaveMatch?.(this.id);

    // Pool
    else if (event === 'pool_shoot') this.engineInstance.shoot?.(this.id, data.angle, data.power);

    // Soccer
    else if (event === 'soccer_input') this.engineInstance.handleInput?.(this.id, data);

    // Bomb Tag
    else if (event === 'bt_input') this.engineInstance.handleInput?.(this.id, data);

    // Battleship
    else if (event === 'bs_strike') this.engineInstance.fireStrike?.(this.id, data.row, data.col);

    // Air Hockey
    else if (event === 'hockey_paddle') this.engineInstance.handlePaddle?.(this.id, data.x, data.y);

    // Archery
    else if (event === 'archery_shoot') this.engineInstance.shootArrow?.(this.id, data.angle, data.power);

    // Trivia
    else if (event === 'trivia_chooseCategory') this.engineInstance.setCategoryAndBegin?.(data.category);
    else if (event === 'trivia_answer') this.engineInstance.submitAnswer?.(this.id, data.choiceIndex);

    // Kart
    else if (event === 'kart_input') this.engineInstance.handleInput?.(this.id, data);

    // Snake
    else if (event === 'snake_steer') this.engineInstance.handleInput?.(this.id, data.angle);
    else if (event === 'snake_respawn') this.engineInstance.respawnSnake?.(this.id);

    // Tank
    else if (event === 'tank_input') this.engineInstance.handleInput?.(this.id, data);
    else if (event === 'tank_turret') this.engineInstance.handleTurret?.(this.id, data.angle);
    else if (event === 'tank_fire') this.engineInstance.fireBullet?.(this.id);

    // Tic-Tac-Toe
    else if (event === 'ttt_move') this.engineInstance.makeMove?.(this.id, data.cellIndex);

    // Connect 4
    else if (event === 'c4_drop') this.engineInstance.dropPiece?.(this.id, data.column);

    // Cyber Pong
    else if (event === 'pong_paddle') this.engineInstance.handlePaddle?.(this.id, data.y);

    // Uno
    else if (event === 'uno_play') this.engineInstance.playCard?.(this.id, data.cardIndex, data.wildColor);
    else if (event === 'uno_draw') this.engineInstance.drawCard?.(this.id);

    // Spy
    else if (event === 'spy_speak') this.engineInstance.submitDescription?.(this.id, data.text);
    else if (event === 'spy_vote') this.engineInstance.castVote?.(this.id, data.targetId);

    // Word Bomb
    else if (event === 'wb_submit') this.engineInstance.submitWord?.(this.id, data.word);

    // Tetris
    else if (event === 'tetris_updateBoard') this.engineInstance.handleBoardUpdate?.(this.id, data.board, data.linesCleared);
    else if (event === 'tetris_dead') this.engineInstance.handlePlayerDeath?.(this.id);

    // Space
    else if (event === 'space_input') this.engineInstance.handleInput?.(this.id, data);

    // Draw & Guess
    else if (event === 'dg_chooseWord') this.engineInstance.chooseWord?.(this.id, data.word);
    else if (event === 'dg_stroke') this.engineInstance.handleStroke?.(this.id, data);
    else if (event === 'dg_clear') this.engineInstance.handleClearCanvas?.(this.id);
  }

  startLocalGame(gameId, options = { targetPlayers: 4, fillBots: true }) {
    if (this.engineInstance?.destroy) {
      this.engineInstance.destroy();
      this.engineInstance = null;
    }

    // When launching directly, use the room's current player list if available
    let currentPlayers = this.room?.players ? [...this.room.players] : [];
    if (currentPlayers.length === 0) {
      currentPlayers = [
        {
          id: this.id,
          name: this.playerName,
          avatar: this.avatar,
          color: '#eab308',
          isHost: true,
          isReady: true,
          isBot: false,
          seatIndex: 0,
          score: 0
        }
      ];
    }

    const duelGames = ['ticTacToe', 'connect4', 'cyberPong', 'airHockey', 'battleship', 'poolGame', 'soccerGame'];
    const target = duelGames.includes(gameId) ? 2 : (options?.targetPlayers || 4);

    if (options?.fillBots !== false && currentPlayers.length < target) {
      while (currentPlayers.length < target) {
        const seatIndex = currentPlayers.length;
        const botIdx = seatIndex % this.botNames.length;
        currentPlayers.push({
          id: `bot_${Date.now()}_${seatIndex}`,
          name: this.botNames[botIdx],
          avatar: this.botAvatars[botIdx],
          color: this.playerColors[seatIndex % this.playerColors.length],
          isHost: false,
          isReady: true,
          isBot: true,
          seatIndex,
          score: 0
        });
      }
    }

    this.room = {
      code: this.room?.code || 'LOCAL_SOLO',
      hostId: this.id,
      currentGame: gameId,
      maxPlayers: 8,
      settings: {
        roundTimer: 60,
        totalRounds: 3,
        targetPlayers: options?.targetPlayers || 4,
        fillBots: options?.fillBots !== false
      },
      players: currentPlayers
    };

    // Instantiate appropriate engine locally
    if (gameId === 'ludoParty') this.engineInstance = new LudoEngine(this.io, this.room);
    else if (gameId === 'poolGame') this.engineInstance = new PoolEngine(this.io, this.room);
    else if (gameId === 'soccerGame') this.engineInstance = new SoccerEngine(this.io, this.room);
    else if (gameId === 'bombTag') this.engineInstance = new BombTagEngine(this.io, this.room);
    else if (gameId === 'battleship') this.engineInstance = new BattleshipEngine(this.io, this.room);
    else if (gameId === 'airHockey') this.engineInstance = new AirHockeyEngine(this.io, this.room);
    else if (gameId === 'archeryClash') this.engineInstance = new ArcheryEngine(this.io, this.room);
    else if (gameId === 'triviaMania') this.engineInstance = new TriviaEngine(this.io, this.room);
    else if (gameId === 'kartRacers') this.engineInstance = new KartEngine(this.io, this.room);
    else if (gameId === 'snakeArena') this.engineInstance = new SnakeEngine(this.io, this.room);
    else if (gameId === 'tankArena') this.engineInstance = new TankEngine(this.io, this.room);
    else if (gameId === 'ticTacToe') this.engineInstance = new TicTacToeEngine(this.io, this.room);
    else if (gameId === 'connect4') this.engineInstance = new Connect4Engine(this.io, this.room);
    else if (gameId === 'cyberPong') this.engineInstance = new PongEngine(this.io, this.room);
    else if (gameId === 'unoCard') this.engineInstance = new UnoEngine(this.io, this.room);
    else if (gameId === 'spyGame') this.engineInstance = new SpyEngine(this.io, this.room);
    else if (gameId === 'wordBomb') this.engineInstance = new WordBombEngine(this.io, this.room);
    else if (gameId === 'tetrisBattle') this.engineInstance = new TetrisEngine(this.io, this.room);
    else if (gameId === 'spaceSurvival') this.engineInstance = new SpaceEngine(this.io, this.room);
    else if (gameId === 'drawGuess') this.engineInstance = new DrawGuessEngine(this.io, this.room);

    setTimeout(() => {
      if (this.engineInstance) {
        if (this.engineInstance.start) this.engineInstance.start();
        else if (this.engineInstance.startRound) this.engineInstance.startRound();
      }
    }, 100);

    return this.room;
  }

  destroy() {
    if (this.engineInstance?.destroy) {
      this.engineInstance.destroy();
      this.engineInstance = null;
    }
  }
}
